/**
 * A line diff of one file before and after an edit, as hunks with a little
 * context: enough to show an author what the assistant changed. The common
 * head and tail are trimmed first, so the longest-common-subsequence table
 * only spans the lines that moved.
 */

export interface DiffLine {
  kind: 'same' | 'added' | 'removed';
  text: string;
  /** line number in the file after the edit; for removed lines, where they were */
  line: number;
}

export interface DiffSummary {
  added: number;
  removed: number;
  /** changed lines with `context` lines around them; `null` entries mark the gaps between hunks */
  lines: Array<DiffLine | null>;
}

const MAX_TABLE = 4_000_000;

export function lineDiff(before: string, after: string, context = 2): DiffSummary {
  const a = before.split('\n');
  const b = after.split('\n');
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head += 1;
  let tail = 0;
  while (tail < a.length - head && tail < b.length - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail += 1;
  const midA = a.slice(head, a.length - tail);
  const midB = b.slice(head, b.length - tail);

  const middle: DiffLine[] = [];
  if (midA.length * midB.length > MAX_TABLE) {
    // too big to align: everything between the common ends is removed, then added
    midA.forEach((text) => middle.push({ kind: 'removed', text, line: head + 1 }));
    midB.forEach((text, i) => middle.push({ kind: 'added', text, line: head + i + 1 }));
  } else {
    const n = midA.length;
    const m = midB.length;
    const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) lcs[i][j] = midA[i] === midB[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
    let i = 0;
    let j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && midA[i] === midB[j]) {
        middle.push({ kind: 'same', text: midA[i], line: head + j + 1 });
        i += 1;
        j += 1;
      } else if (i < n && (j >= m || lcs[i + 1][j] >= lcs[i][j + 1])) {
        // what went comes before what replaced it
        middle.push({ kind: 'removed', text: midA[i], line: head + j + 1 });
        i += 1;
      } else {
        middle.push({ kind: 'added', text: midB[j], line: head + j + 1 });
        j += 1;
      }
    }
  }

  const all: DiffLine[] = [
    ...b.slice(0, head).map((text, i) => ({ kind: 'same' as const, text, line: i + 1 })),
    ...middle,
    ...b.slice(b.length - tail).map((text, i) => ({ kind: 'same' as const, text, line: b.length - tail + i + 1 })),
  ];
  const keep = all.map(() => false);
  all.forEach((l, i) => {
    if (l.kind === 'same') return;
    for (let k = Math.max(0, i - context); k <= Math.min(all.length - 1, i + context); k += 1) keep[k] = true;
  });
  const lines: Array<DiffLine | null> = [];
  all.forEach((l, i) => {
    if (keep[i]) lines.push(l);
    else if (lines.length > 0 && lines[lines.length - 1] !== null) lines.push(null);
  });
  if (lines[lines.length - 1] === null) lines.pop();
  return { added: all.filter((l) => l.kind === 'added').length, removed: all.filter((l) => l.kind === 'removed').length, lines };
}
